import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormArray, AbstractControl, FormControl } from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { SettingsService } from 'src/app/views/settings/settings.service';
import { FullLoaderService } from 'src/app/shared/services/full-loader.service';

@Component({
  selector: 'app-supplier-service-purchase-order-details',
  templateUrl: './supplier-service-purchase-order-details.component.html',
  styleUrls: ['./supplier-service-purchase-order-details.component.css']
})
export class SupplierServicePurchaseOrderDetailsComponent implements OnInit {

  // Section expand/collapse flags
  isForm1Expand: boolean = true;
  isForm2Expand: boolean = true;
  isForm3Expand: boolean = true;
  isForm4Expand: boolean = true;
  isForm5Expand: boolean = true;
  isForm6Expand: boolean = true;
  isForm7Expand: boolean = true;
  isForm8Expand: boolean = true;
  isForm9Expand: boolean = true;
  isForm10Expand: boolean = true;

  // Route params
  purchaseOrderId!: number;

  // Form groups
  costSummaryFormGroup!: UntypedFormGroup;
  headerFormGroup!: UntypedFormGroup;
  paymentFormGroup!: UntypedFormGroup;
  lineItemTableForm!: UntypedFormGroup;

  // Data
  dataSource: any[] = [];
  headerResponse: any = null;
  uploadedAdditionalAttachmentsArray: any[] = [];

  // Carousel
  previewSection: boolean = false;
  previewDoc: any[] = [];
  currentIndex = 0;

  // Freight toggle
  showFreightDetails: boolean = false;

  // Current date for exchange rate display
  currentdate = new Date();

  // Supplier
  buyerLogo: string = '';
  supplierLogo: string = '';

  // Accept/Reject workflow
  showAcceptOrder: boolean = false;
  showRejectOrder: boolean = false;
  showOrderConfirmed: boolean = false;
  orderConfirmationNumber: string = '';
  deliveryDate!: Date;
  shippingDate!: Date;
  rejectionReasons: { id: number; reason: string }[] = [];
  rejectionReason!: number;
  rejectionMessage: string = '';

  // Extra header fields for accept/reject
  buyerId!: number;
  rfqId!: number;
  quotationId!: number;
  buyerName: string = '';
  supplierName: string = '';

  reviewTooltip = `Please review the PO thoroughly before clicking Accept. Once the Purchase Order (PO) is accepted, it cannot be modified or changed.`;

  constructor(
    private snack: MatSnackBar,
    private _formBuilder: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private cookieService: CookieService,
    private router: Router,
    private settingsService: SettingsService,
    private fullLoaderService: FullLoaderService,
    private activatedRoute: ActivatedRoute
  ) {}

  get rowControls(): AbstractControl[] {
    return (this.lineItemTableForm.get('rows') as UntypedFormArray).controls;
  }

  getRowControl(rowIndex: number, field: string): FormControl {
    return this.rowControls[rowIndex].get(field) as FormControl;
  }

  async ngOnInit() {
    this.fullLoaderService.showLoader();

    // Initialize form groups
    this.costSummaryFormGroup = this._formBuilder.group({
      lineItemListPriceSubtotal: [0],
      lineItemDiscount: [0],
      lineItemSubtotal: [0],
      generalDiscountType: [''],
      generalDiscountValue: [0],
      lineItemApplicableTaxProfile: [''],
      lineItemTaxAmount: [0],
      quoteLineItemFreight: [[]],
      lineItemFreightTotal: [0],
      lineItemTotal: [0],
      currency: [null]
    });

    this.headerFormGroup = this._formBuilder.group({
      // PO Details
      purchaseOrderRefNumber: [''],
      purchaseOrderDate: [''],
      rfqRefNumber: [''],
      rfqDate: [''],
      quoteRefNumber: [''],
      quotationDate: [''],
      expectedDeliveryDate: [''],
      // Buyer Company
      buyerCompanyName: [''],
      buyerEmail: [''],
      buyerPhone: [''],
      buyerFax: [''],
      buyerAddress: [''],
      // Buyer Contact
      buyerContactPerson: [''],
      buyerContactEmail: [''],
      buyerContactPhone: [''],
      buyerJobTitle: [''],
      buyerContactFax: [''],
      buyerContactAddress: [''],
      // Supplier
      supplierContactName: [''],
      supplierEmail: [''],
      supplierPhone: [''],
      supplierFax: [''],
      supplierCompanyEmail: [''],
      supplierCompanyName: [''],
      supplierAddress: [''],
      // Vessel
      vesselName: [''],
      vesselIMONumber: [''],
      vesselType: [''],
      vesselClass: [''],
      // Port
      portName: [''],
      portCode: [''],
      vesselETA: [''],
      vesselETD: [''],
      vesselEtaTime: [''],
      vesselEtdTime: ['']
    });

    this.paymentFormGroup = this._formBuilder.group({
      // Buyer Payment Terms
      billingAddress: [''],
      applicableTaxProfile: [''],
      buyersPaymentTerms: [''],
      buyerTermsAndConditions: [''],
      // Port Agent
      portAgencyName: [''],
      portAgentName: [''],
      portAgentPhoneNumber: [''],
      portAgentContact: [''],
      portAgentPhoneFax: [''],
      portAgentEmail: [''],
      portAgentAddress: [''],
      // T&C
      termsAndConditions: ['']
    });

    this.lineItemTableForm = this._formBuilder.group({
      rows: this._formBuilder.array([])
    });

    const params = this.activatedRoute.snapshot.queryParams;

    if (params['purchaseOrderId']) {
      this.purchaseOrderId = params['purchaseOrderId'];
      await this.loadPurchaseOrderData();
    }

    this.fullLoaderService.hideLoader();
  }

  async loadPurchaseOrderData() {
    try {
      // Fetch all 3 sections in parallel
      const [lineItemsResult, headerResult, paymentTermsResult] = await Promise.all([
        this.settingsService.fetchServicePurchaseOrder({
          purchaseOrderId: this.purchaseOrderId,
          section: 'lineItems'
        }),
        this.settingsService.fetchServicePurchaseOrder({
          purchaseOrderId: this.purchaseOrderId,
          section: 'header'
        }),
        this.settingsService.fetchServicePurchaseOrder({
          purchaseOrderId: this.purchaseOrderId,
          section: 'paymentTerms'
        })
      ]);

      // Populate line items + cost summary
      if (lineItemsResult && lineItemsResult.length > 0) {
        this.populateLineItems(lineItemsResult[0]);
      }

      // Populate header
      if (headerResult && headerResult.length > 0) {
        this.headerResponse = headerResult[0];
        this.populateHeader(headerResult[0]);
      }

      // Populate payment terms
      if (paymentTermsResult && paymentTermsResult.length > 0) {
        this.populatePaymentTerms(paymentTermsResult[0]);
      }
    } catch (error: any) {
      console.error('Error loading service PO data:', error);
      this.snack.open(error?.message || 'Failed to load Purchase Order', 'OK', { duration: 3000 });
    }
  }

  populateLineItems(lineData: any) {
    // Cost summary
    let currency = lineData.currency;
    if (typeof currency === 'string') {
      try { currency = JSON.parse(currency); } catch (e) { currency = null; }
    }

    let freight = lineData.quoteLineItemFreight;
    if (typeof freight === 'string') {
      try { freight = JSON.parse(freight); } catch (e) { freight = []; }
    }

    this.costSummaryFormGroup.patchValue({
      lineItemListPriceSubtotal: lineData.lineItemListPriceSubtotal || 0,
      lineItemDiscount: lineData.lineItemDiscount || 0,
      lineItemSubtotal: lineData.lineItemSubtotal || 0,
      generalDiscountType: lineData.generalDiscountType || '',
      generalDiscountValue: lineData.generalDiscountValue || 0,
      lineItemApplicableTaxProfile: lineData.lineItemApplicableTaxProfile || '',
      lineItemTaxAmount: lineData.lineItemTaxAmount || 0,
      quoteLineItemFreight: freight || [],
      lineItemFreightTotal: lineData.lineItemFreightTotal || 0,
      lineItemTotal: lineData.lineItemTotal || 0,
      currency: currency
    });

    // Line items table
    let quotationLineItems = lineData.quotationLineItems;
    if (typeof quotationLineItems === 'string') {
      try { quotationLineItems = JSON.parse(quotationLineItems); } catch (e) { quotationLineItems = []; }
    }

    if (quotationLineItems && quotationLineItems.length > 0) {
      this.dataSource = quotationLineItems;
      this.populateForm(quotationLineItems);
    }
  }

  populateForm(data: any[]) {
    const rows = this.lineItemTableForm.get('rows') as UntypedFormArray;
    rows.clear();

    data.forEach((item: any, index: number) => {
      rows.push(this._formBuilder.group({
        position: [index + 1],
        serviceName: [item.serviceName || ''],
        serviceDescription: [item.serviceDescription || ''],
        scopeOfWork: [item.scopeOfWork || ''],
        buyerComment: [item.buyerComment || ''],
        supplierComment: [item.supplierComment || ''],
        uom: [item.uom || ''],
        supplierUom: [item.supplierUom || ''],
        quantity: [item.quantity || 0],
        supplierQuantity: [item.supplierQuantity || 0],
        unitCost: [item.unitCost || 0],
        discount: [item.discount || 0],
        discountedUnitPrice: [item.discountedUnitPrice || 0],
        totalCost: [item.totalCost || 0],
        leadTime: [item.leadTime || ''],
        isActive: [item.isActive]
      }));
    });
  }

  populateHeader(header: any) {
    this.buyerLogo = header.buyerOrganizationLogo || '';
    this.supplierLogo = header.supplierOrganizationLogo || '';
    this.buyerId = header.buyerId;
    this.rfqId = header.serviceRfqId;
    this.quotationId = header.serviceQuotationId;
    this.buyerName = header.buyerName || '';
    this.supplierName = header.supplierName || '';
    this.headerFormGroup.patchValue({
      purchaseOrderRefNumber: header.purchaseOrderRefNumber,
      purchaseOrderDate: header.purchaseOrderDate,
      rfqRefNumber: header.rfqRefNumber,
      rfqDate: header.rfqDate,
      quoteRefNumber: header.quoteRefNumber,
      quotationDate: header.quotationDate,
      expectedDeliveryDate: header.expectedDeliveryDate,
      buyerCompanyName: header.buyerCompanyName,
      buyerEmail: header.buyerEmail,
      buyerPhone: header.buyerPhone,
      buyerFax: header.companyFax,
      buyerAddress: header.companyAddress,
      buyerContactPerson: header.buyerContactPerson,
      buyerContactEmail: header.buyerEmail,
      buyerContactPhone: header.buyerPhone,
      buyerJobTitle: header.buyerJobTitle,
      buyerContactFax: header.buyerFax,
      buyerContactAddress: header.buyerAddress,
      supplierContactName: header.supplierName,
      supplierEmail: header.supplierEmail,
      supplierPhone: header.supplierPhone,
      supplierFax: header.supplierFax,
      supplierCompanyEmail: header.supplierCompanyEmail,
      supplierCompanyName: header.supplierCompanyName,
      supplierAddress: header.supplierAddress,
      vesselName: header.vesselName,
      vesselIMONumber: header.vesselImoNumber,
      vesselType: header.vesselType,
      vesselClass: header.class,
      portName: header.portName,
      portCode: header.portCode,
      vesselETA: header.vesselEta,
      vesselETD: header.vesselEtd,
      vesselEtaTime: header.vesselEtaTime,
      vesselEtdTime: header.vesselEtdTime
    });
  }

  populatePaymentTerms(payment: any) {
    this.paymentFormGroup.patchValue({
      billingAddress: payment.billingAddress,
      applicableTaxProfile: payment.applicableTaxProfile,
      buyersPaymentTerms: payment.buyersPaymentTerms,
      buyerTermsAndConditions: payment.termsAndConditions,
      portAgencyName: payment.portAgencyName,
      portAgentName: payment.portAgentName,
      portAgentPhoneNumber: payment.portAgentPhoneNumber,
      portAgentContact: payment.portAgentContact,
      portAgentPhoneFax: payment.portAgentPhoneFax,
      portAgentEmail: payment.emailId,
      portAgentAddress: payment.address,
      termsAndConditions: payment.termsAndConditions
    });

    let attachments = payment.paymentTermsAttachments;
    if (typeof attachments === 'string') {
      try { attachments = JSON.parse(attachments); } catch (e) { attachments = []; }
    }
    this.uploadedAdditionalAttachmentsArray = attachments || [];
  }

  // Download PO PDF
  async downloadPO() {
    this.fullLoaderService.showLoader();
    try {
      const payload = this.buildServicePurchaseOrderPayload();
      const pdf = await this.settingsService.generateServicePoPdf(payload);

      const uint8Array = new Uint8Array(pdf.data);
      const blob = new Blob([uint8Array], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = 'PO-' + (this.headerFormGroup.get('purchaseOrderRefNumber')?.value || 'ServicePO') + '.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Download PO error:', error);
      this.snack.open(error?.message || 'Failed to download PO', 'OK', { duration: 3000 });
    }
    this.fullLoaderService.hideLoader();
  }

  private buildServicePurchaseOrderPayload(): any {
    const header = this.headerFormGroup?.value || {};
    const payment = this.paymentFormGroup?.value || {};
    const pricing = this.costSummaryFormGroup?.value || {};
    const lineItemsFormArray = (this.lineItemTableForm?.get('rows') as UntypedFormArray);
    const lineItems = lineItemsFormArray.controls.map((ctrl: any) => ctrl.value);

    const freightArr = Array.isArray(pricing.quoteLineItemFreight) ? pricing.quoteLineItemFreight : [];

    return {
      ids: {
        purchaseOrderId: this.purchaseOrderId,
        rfqId: this.headerResponse?.serviceRfqId,
        quoteId: this.headerResponse?.serviceQuotationId
      },
      purchaseOrder: {
        purchaseOrderRefNumber: header.purchaseOrderRefNumber,
        purchaseOrderDate: header.purchaseOrderDate,
        rfqRefNumber: header.rfqRefNumber,
        rfqDate: header.rfqDate,
        quoteRefNumber: header.quoteRefNumber,
        quotationDate: header.quotationDate,
        expectedDeliveryDate: header.expectedDeliveryDate
      },
      buyer: {
        company: {
          name: header.buyerCompanyName,
          email: header.buyerEmail,
          phone: header.buyerPhone,
          fax: header.buyerFax,
          address: header.buyerAddress
        },
        contact: {
          person: header.buyerContactPerson,
          email: header.buyerContactEmail,
          phone: header.buyerContactPhone,
          fax: header.buyerContactFax,
          address: header.buyerContactAddress
        }
      },
      supplierDetails: {
        company: {
          name: header.supplierCompanyName,
          email: header.supplierCompanyEmail,
          phone: header.supplierPhone,
          fax: header.supplierFax,
          address: header.supplierAddress
        },
        contact: {
          contactName: header.supplierContactName,
          email: header.supplierEmail,
          phone: header.supplierPhone,
          fax: header.supplierFax
        }
      },
      vessel: {
        name: header.vesselName,
        imoNumber: header.vesselIMONumber,
        type: header.vesselType,
        class: header.vesselClass
      },
      port: {
        name: header.portName,
        code: header.portCode,
        eta: header.vesselETA,
        etd: header.vesselETD
      },
      portAgent: {
        portAgencyName: payment.portAgencyName,
        name: payment.portAgentName,
        phoneNumber: payment.portAgentPhoneNumber,
        contact: payment.portAgentContact,
        phoneFax: payment.portAgentPhoneFax,
        email: payment.portAgentEmail,
        address: payment.portAgentAddress
      },
      priceDetails: {
        currency: pricing.currency,
        listPriceSubtotal: pricing.lineItemListPriceSubtotal,
        discount: pricing.lineItemDiscount,
        subtotal: pricing.lineItemSubtotal,
        generalDiscount: {
          type: pricing.generalDiscountType,
          value: pricing.generalDiscountValue
        },
        freight: freightArr,
        freightTotal: pricing.lineItemFreightTotal,
        applicableTaxProfile: pricing.lineItemApplicableTaxProfile,
        taxAmount: pricing.lineItemTaxAmount,
        total: pricing.lineItemTotal
      },
      termsAndConditions: {
        billingAddress: payment.billingAddress,
        applicableTaxProfile: payment.applicableTaxProfile,
        buyersPaymentTerms: payment.buyersPaymentTerms,
        text: payment.termsAndConditions
      },
      lineItems: lineItems.map((li: any, idx: number) => ({
        position: li.position ?? idx + 1,
        serviceName: li.serviceName ?? '',
        serviceDescription: li.serviceDescription ?? '',
        buyerComment: li.buyerComment ?? '',
        supplierComment: li.supplierComment ?? '',
        uom: li.uom ?? '',
        supplierUom: li.supplierUom ?? '',
        quantity: li.quantity ?? 0,
        supplierQuantity: li.supplierQuantity ?? 0,
        unitCost: li.unitCost ?? 0,
        discount: li.discount ?? 0,
        discountedUnitPrice: li.discountedUnitPrice ?? 0,
        totalCost: li.totalCost ?? 0,
        leadTime: li.leadTime ?? '',
        isActive: li.isActive ?? 1
      })),
      documents: {
        poPdfPath: null
      }
    };
  }

  // Section toggle
  toggleVisibility(section: string) {
    switch (section) {
      case 'form1': this.isForm1Expand = !this.isForm1Expand; break;
      case 'form2': this.isForm2Expand = !this.isForm2Expand; break;
      case 'form3': this.isForm3Expand = !this.isForm3Expand; break;
      case 'form4': this.isForm4Expand = !this.isForm4Expand; break;
      case 'form5': this.isForm5Expand = !this.isForm5Expand; break;
      case 'form6': this.isForm6Expand = !this.isForm6Expand; break;
      case 'form7': this.isForm7Expand = !this.isForm7Expand; break;
      case 'form8': this.isForm8Expand = !this.isForm8Expand; break;
      case 'form9': this.isForm9Expand = !this.isForm9Expand; break;
      case 'form10': this.isForm10Expand = !this.isForm10Expand; break;
    }
  }

  toggleFreightDetails() {
    this.showFreightDetails = !this.showFreightDetails;
  }

  // Close and go back to supplier service PO list
  close() {
    this.router.navigate([
      'operations', 'supplier-service-purchase-order-list'
    ]);
  }

  // Attachment preview carousel
  getHeaderAttachmentCount(): number {
    return this.uploadedAdditionalAttachmentsArray?.length || 0;
  }

  togglePreview(show: boolean, type: string) {
    if (show && type === 'generalAttachment') {
      this.previewDoc = this.uploadedAdditionalAttachmentsArray.map((att: any) => ({
        ...att,
        dataUrl: att.dataUrl || att.s3_path || ''
      }));
      this.currentIndex = 0;
    }
    this.previewSection = show;
  }

  isPdf(url: string): boolean {
    return url?.toLowerCase()?.endsWith('.pdf') || false;
  }

  prevSlide() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    }
  }

  nextSlide() {
    if (this.currentIndex < this.previewDoc.length - 1) {
      this.currentIndex++;
    }
  }

  // Create Invoice navigation
  createInvoice() {
    this.router.navigate(['/operations/create-service-invoice'], {
      queryParams: {
        servicePurchaseOrderId: this.purchaseOrderId,
        serviceRfqId: this.rfqId,
        serviceQuotationId: this.quotationId,
        buyerId: this.buyerId,
      },
    });
  }

  // Accept/Reject workflow methods

  async acceptPO() {
    this.fullLoaderService.showLoader();
    await this.getLastAcceptedOrderId();
    this.fullLoaderService.hideLoader();
    this.showAcceptOrder = true;
  }

  async rejectPO() {
    this.fullLoaderService.showLoader();
    await this.fetchAllRejectionReason();
    this.fullLoaderService.hideLoader();
    this.showRejectOrder = true;
  }

  async getLastAcceptedOrderId() {
    try {
      const result = await this.settingsService.getLastAcceptedServiceOrderId();
      console.log({ result });
      const lastId = (result[0]?.lastId ?? 0) + 1;

      const orderConfirmationNumber = `OC-${this.headerFormGroup.get('purchaseOrderRefNumber')?.value}-${lastId
        .toString()
        .padStart(2, '0')}`;

      console.log({
        lastId,
        orderConfirmationNumber,
      });

      this.orderConfirmationNumber = orderConfirmationNumber;
    } catch (error) {
      console.error('Error getting last accepted order id:', error);
      throw error;
    }
  }

  async fetchAllRejectionReason() {
    try {
      const result: any = await this.settingsService.fetchAllServiceRejectionReason();
      console.log({ result });

      this.rejectionReasons = result.map((r: any) => ({
        id: r.id,
        reason: r.reason
      }));

      console.log('rejectionReasons', this.rejectionReasons);
    } catch (error) {
      console.error('Error getting Rejection Reason:', error);
      throw error;
    }
  }

  async acceptOrder() {
    const poPayload = this.buildServicePurchaseOrderPayload();
    console.log({ poPayload });

    const params = {
      orderConfirmationNumber: this.orderConfirmationNumber,
      estimatedDeliveryDate: this.deliveryDate || null,
      estimatedShippingDate: this.shippingDate || null,
      purchaseOrderRefNumber: this.headerFormGroup.get('purchaseOrderRefNumber')?.value,
      buyerName: this.buyerName,
      rfqRefNumber: this.headerFormGroup.get('rfqRefNumber')?.value,
      supplierName: this.supplierName,
      buyerEmail: this.headerFormGroup.get('buyerEmail')?.value,
      supplierEmail: this.headerFormGroup.get('supplierEmail')?.value,
      buyerOrganizationLogo: this.buyerLogo,
      supplierOrganizationLogo: this.supplierLogo,
      buyerId: this.buyerId,
      supplierId: Number(this.cookieService.get('userId')),
      rfqId: Number(this.rfqId),
      quotationId: Number(this.quotationId),
      purchaseOrderId: Number(this.purchaseOrderId),
      supplierCompanyName: this.headerFormGroup.get('supplierCompanyName')?.value,
      pdfPayload: poPayload,
    };

    console.log('Order Accepted:', { params });
    this.showAcceptOrder = false;
    this.fullLoaderService.showLoader();
    await this.settingsService.sendServicePOConfirmationAcknowledgement(params);
    await this.downloadPO();
    this.fullLoaderService.hideLoader();
    this.showOrderConfirmed = true;
  }

  async rejectOrder() {
    console.log('Selected Reason ID:', this.rejectionReason);
    console.log('Message:', this.rejectionMessage);

    const selectedReason = this.rejectionReasons.find(
      r => r.id === Number(this.rejectionReason)
    );

    console.log('selectedReason', selectedReason);

    const poPayload = this.buildServicePurchaseOrderPayload();
    console.log({ poPayload });

    const params = {
      reasonId: this.rejectionReason,
      rejectReason: selectedReason?.reason,
      rejectionMessage: this.rejectionMessage,
      purchaseOrderRefNumber: this.headerFormGroup.get('purchaseOrderRefNumber')?.value,
      buyerName: this.buyerName,
      rfqRefNumber: this.headerFormGroup.get('rfqRefNumber')?.value,
      supplierName: this.supplierName,
      buyerEmail: this.headerFormGroup.get('buyerEmail')?.value,
      supplierEmail: this.headerFormGroup.get('supplierEmail')?.value,
      buyerOrganizationLogo: this.buyerLogo,
      supplierOrganizationLogo: this.supplierLogo,
      buyerId: this.buyerId,
      supplierId: Number(this.cookieService.get('userId')),
      rfqId: Number(this.rfqId),
      quotationId: Number(this.quotationId),
      purchaseOrderId: Number(this.purchaseOrderId),
      supplierCompanyName: this.headerFormGroup.get('supplierCompanyName')?.value,
      pdfPayload: poPayload,
      alternativeSuggested: 'No'
    };

    console.log('Order Rejected:', { params });
    this.showRejectOrder = false;
    this.fullLoaderService.showLoader();
    await this.settingsService.sendServicePORejectionAcknowledgement(params);
    this.fullLoaderService.hideLoader();

    this.router.navigate(['/operations/supplier-service-purchase-order-list'], {
      queryParams: {
        selectedStatus: 'Rejected',
        label: 'Order',
      },
    });
  }

  cancel() {
    this.showAcceptOrder = false;
    this.showRejectOrder = false;
    this.showOrderConfirmed = false;
  }

  closeAcceptOrderConfirmation() {
    this.showOrderConfirmed = false;
    this.router.navigate(['/operations/supplier-service-purchase-order-list'], {
      queryParams: {
        selectedStatus: 'Accepted',
        label: 'Order',
      },
    });
  }

  goToConfirmedOrderOverview() {
    this.router.navigate([
      'settings', 'inbox'
    ]);
  }
}
